import { createTheme } from '@mui/material/styles';

// Colores de la paleta ComHub
const colors = {
  primary: {
    main: '#002642',        // CMYK 98/93/53/35 - Azul muy oscuro
    dark: '#001829',        // Versión más oscura
    light: '#00577A',       // CMYK 98/69/42/3 - Azul oscuro principal
    contrastText: '#FFFFFF'
  },
  secondary: {
    main: '#DB9600',        // CMYK 8/51/98/0 - Amarillo/Dorado
    dark: '#B8790',         // Versión más oscura
    light: '#F4B942',       // Versión más clara
    contrastText: '#002642'
  },
  neutral: {
    main: '#75787B',        // RGB 117/120/123 - Gris neutro
    dark: '#5A5D60',
    light: '#9E9E9E',
    contrastText: '#FFFFFF'
  },
  success: {
    main: '#4CAF50',
    contrastText: '#FFFFFF'
  },
  warning: {
    main: '#FF9800',
    contrastText: '#FFFFFF'
  },
  error: {
    main: '#F44336',
    contrastText: '#FFFFFF'
  },
  info: {
    main: '#2196F3',
    contrastText: '#FFFFFF'
  }
};

// Tipografías
const typography = {
  fontFamily: [
    'Creato Display',
    'Inter',
    'Helvetica Neue',
    'Arial',
    'sans-serif'
  ].join(','),
  
  // Títulos y textos cortos - BATANGAS BOLD (usando Inter Bold como fallback)
  h1: {
    fontFamily: 'Batangas Bold, Inter, Helvetica Neue, Arial, sans-serif',
    fontWeight: 700,
    fontSize: '3rem',
    lineHeight: 1.25,
    letterSpacing: '-0.025em',
    '@media (max-width:640px)': {
      fontSize: '2.5rem'
    }
  },
  h2: {
    fontFamily: 'Batangas Bold, Inter, Helvetica Neue, Arial, sans-serif',
    fontWeight: 700,
    fontSize: '2.25rem',
    lineHeight: 1.25,
    letterSpacing: '-0.025em',
    '@media (max-width:640px)': {
      fontSize: '2rem'
    }
  },
  h3: {
    fontFamily: 'Batangas Bold, Inter, Helvetica Neue, Arial, sans-serif',
    fontWeight: 700,
    fontSize: '1.875rem',
    lineHeight: 1.25,
    letterSpacing: '-0.025em',
    '@media (max-width:640px)': {
      fontSize: '1.6rem'
    }
  },
  h4: {
    fontFamily: 'Batangas Bold, Inter, Helvetica Neue, Arial, sans-serif',
    fontWeight: 700,
    fontSize: '1.5rem',
    lineHeight: 1.25,
    letterSpacing: 'normal',
    '@media (max-width:640px)': {
      fontSize: '1.3rem'
    }
  },
  h5: {
    fontFamily: 'Batangas Bold, Inter, Helvetica Neue, Arial, sans-serif',
    fontWeight: 600,
    fontSize: '1.25rem',
    lineHeight: 1.25,
    letterSpacing: 'normal',
    '@media (max-width:640px)': {
      fontSize: '1.1rem'
    }
  },
  h6: {
    fontFamily: 'Batangas Bold, Inter, Helvetica Neue, Arial, sans-serif',
    fontWeight: 600,
    fontSize: '1.125rem',
    lineHeight: 1.25,
    letterSpacing: 'normal',
    '@media (max-width:640px)': {
      fontSize: '1rem'
    }
  },
  
  // Texto corrido y textos pequeños - CREATO DISPLAY (usando Inter como fallback)
  body1: {
    fontFamily: 'Creato Display, Inter, Helvetica Neue, Arial, sans-serif',
    fontWeight: 400,
    fontSize: '1rem',
    lineHeight: 1.5,
    letterSpacing: 'normal',
    '@media (max-width:640px)': {
      fontSize: '0.9rem'
    }
  },
  body2: {
    fontFamily: 'Creato Display, Inter, Helvetica Neue, Arial, sans-serif',
    fontWeight: 400,
    fontSize: '0.875rem',
    lineHeight: 1.5,
    letterSpacing: 'normal',
    '@media (max-width:640px)': {
      fontSize: '0.8rem'
    }
  },
  subtitle1: {
    fontFamily: 'Creato Display, Inter, Helvetica Neue, Arial, sans-serif',
    fontWeight: 500,
    fontSize: '1rem',
    lineHeight: 1.75,
    letterSpacing: '0.025em'
  },
  subtitle2: {
    fontFamily: 'Creato Display, Inter, Helvetica Neue, Arial, sans-serif',
    fontWeight: 500,
    fontSize: '0.875rem',
    lineHeight: 1.5,
    letterSpacing: '0.025em'
  },
  caption: {
    fontFamily: 'Creato Display, Inter, Helvetica Neue, Arial, sans-serif',
    fontWeight: 400,
    fontSize: '0.75rem',
    lineHeight: 1.66,
    letterSpacing: '0.05em',
    '@media (max-width:640px)': {
      fontSize: '0.7rem'
    }
  },
  overline: {
    fontFamily: 'Creato Display, Inter, Helvetica Neue, Arial, sans-serif',
    fontWeight: 500,
    fontSize: '0.75rem',
    lineHeight: 2.66,
    letterSpacing: '0.1em',
    textTransform: 'uppercase'
  },
  button: {
    fontFamily: 'Batangas Bold, Inter, Helvetica Neue, Arial, sans-serif',
    fontWeight: 600,
    fontSize: '1rem',
    lineHeight: 1.75,
    letterSpacing: '0.025em',
    textTransform: 'none'
  }
};

// Sombras personalizadas
const shadows = [
  'none',
  '0 2px 4px rgba(0, 38, 66, 0.1)',      // shadow-sm
  '0 4px 12px rgba(0, 38, 66, 0.15)',    // shadow-md
  '0 8px 24px rgba(0, 38, 66, 0.2)',     // shadow-lg
  '0 16px 48px rgba(0, 38, 66, 0.25)',   // shadow-xl
  '0 2px 4px rgba(219, 150, 0, 0.1)',    // shadow-secondary-sm
  '0 4px 12px rgba(219, 150, 0, 0.15)',  // shadow-secondary-md
  '0 8px 24px rgba(219, 150, 0, 0.2)',   // shadow-secondary-lg
  '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
  '0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23)',
  '0 10px 20px rgba(0, 0, 0, 0.19), 0 6px 6px rgba(0, 0, 0, 0.23)',
  '0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22)',
  '0 19px 38px rgba(0, 0, 0, 0.30), 0 15px 12px rgba(0, 0, 0, 0.22)',
  '0 2px 4px rgba(0, 38, 66, 0.1)',
  '0 4px 12px rgba(0, 38, 66, 0.15)',
  '0 8px 24px rgba(0, 38, 66, 0.2)',
  '0 16px 48px rgba(0, 38, 66, 0.25)',
  '0 2px 4px rgba(219, 150, 0, 0.1)',
  '0 4px 12px rgba(219, 150, 0, 0.15)',
  '0 8px 24px rgba(219, 150, 0, 0.2)',
  '0 16px 48px rgba(219, 150, 0, 0.25)',
  '0 8px 32px rgba(0, 38, 66, 0.3)',
  '0 12px 40px rgba(0, 38, 66, 0.35)',
  '0 16px 48px rgba(0, 38, 66, 0.4)',
  '0 20px 56px rgba(0, 38, 66, 0.45)'
];

// Configuración del tema
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: colors.primary,
    secondary: colors.secondary,
    success: colors.success,
    warning: colors.warning,
    error: colors.error,
    info: colors.info,
    background: {
      default: '#FAFAFA',
      paper: '#FFFFFF'
    },
    text: {
      primary: colors.primary.main,
      secondary: colors.neutral.main,
    },
    // Colores personalizados adicionales
    neutral: colors.neutral
  },
  
  typography,
  
  shadows,
  
  shape: {
    borderRadius: 12
  },
  
  spacing: 8,
  
  components: {
    // Button
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 24px',
          fontSize: '1rem',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: '0 4px 12px rgba(0, 38, 66, 0.15)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(0, 38, 66, 0.2)'
          }
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.light} 100%)`,
          color: colors.primary.contrastText,
          '&:hover': {
            background: `linear-gradient(135deg, ${colors.primary.dark} 0%, ${colors.primary.main} 100%)`,
          }
        },
        containedSecondary: {
          background: `linear-gradient(135deg, ${colors.secondary.main} 0%, ${colors.secondary.light} 100%)`,
          color: colors.secondary.contrastText,
          boxShadow: '0 4px 12px rgba(219, 150, 0, 0.15)',
          '&:hover': {
            background: `linear-gradient(135deg, ${colors.secondary.dark} 0%, ${colors.secondary.main} 100%)`,
            boxShadow: '0 8px 24px rgba(219, 150, 0, 0.2)'
          }
        }
      }
    },
    
    // Paper (Cards)
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          border: `1px solid rgba(117, 120, 123, 0.1)`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 24px rgba(0, 38, 66, 0.2)'
          }
        }
      }
    },
    
    // TextField
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            transition: 'all 0.3s ease-in-out',
            '& fieldset': {
              borderColor: 'rgba(117, 120, 123, 0.3)',
              borderWidth: '2px'
            },
            '&:hover fieldset': {
              borderColor: colors.primary.light
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.primary.main,
              borderWidth: '2px'
            }
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: colors.primary.main
          }
        }
      }
    },
    
    // AppBar
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.light} 100%)`,
          boxShadow: '0 4px 12px rgba(0, 38, 66, 0.15)'
        }
      }
    },
    
    // Drawer
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: `linear-gradient(180deg, ${colors.primary.main} 0%, ${colors.primary.light} 100%)`,
          border: 'none'
        }
      }
    },
    
    // List Items (para menú)
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          margin: '4px 8px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            transform: 'translateX(4px)'
          }
        }
      }
    },
    
    // Chips
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          fontWeight: 500
        }
      }
    },
    
    // Tabs
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '1rem'
        }
      }
    }
  }
});

export default theme;